// iter144 — On-mount fetch of admin-configured rate cards + apply to
// mealPlan.js runtime overrides so Daily Entry / Reports / MDCF / UC all
// use the same source of truth. Small module-level cache to avoid a
// refetch storm when multiple consumers mount at the same time.
import { useEffect } from "react";
import axios from "axios";
import { setRateCardOverrides } from "@/lib/mealPlan";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// iter145 — TTL-based cache so school users pick up admin's rate-card
// edits within a minute even without a hard page refresh.
const CACHE_TTL_MS = 30 * 1000;
let _cache = null;
let _cacheTs = 0;
let _inflight = null;

/** Pick the rate card that covers today for each class. */
function pickActive(cards) {
  if (!Array.isArray(cards) || !cards.length) return [];
  const today = new Date().toISOString().slice(0, 10);
  // Prefer cards whose effective window covers today; else the most
  // recent one by effectiveFrom.
  const inWindow = cards.filter((c) => {
    const from = (c.effectiveFrom || "").slice(0, 10);
    const to = (c.effectiveTo || "").slice(0, 10);
    if (from && today < from) return false;
    if (to && today > to) return false;
    return true;
  });
  const source = inWindow.length ? inWindow : cards;
  // Group by className, keep the newest effectiveFrom.
  const byClass = new Map();
  for (const c of source) {
    const prev = byClass.get(c.className);
    if (!prev || (c.effectiveFrom || "") > (prev.effectiveFrom || "")) {
      byClass.set(c.className, c);
    }
  }
  return Array.from(byClass.values());
}

async function loadOnce(force = false) {
  const fresh = _cache && (Date.now() - _cacheTs) < CACHE_TTL_MS;
  if (fresh && !force) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const r = await axios.get(`${API}/rate-cards`);
      const cards = Array.isArray(r.data) ? r.data : [];
      const active = pickActive(cards);
      setRateCardOverrides(active);
      _cache = active;
      _cacheTs = Date.now();
      return active;
    } catch (err) {
      console.warn("[rate-cards] sync failed, using defaults:", err?.message || err);
      _cache = [];
      _cacheTs = Date.now();
      return [];
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

export function useRateCardsSync() {
  useEffect(() => { loadOnce(); }, []);
}

/** Await before computing consumption so every page uses the admin rate cards. */
export function ensureRateCards() { return loadOnce(); }

/** Force a refresh — call after admin saves a rate card. */
export function refreshRateCards() { return loadOnce(true); }
